import { forwardRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { clsx } from 'clsx';

/**
 * Campo de entrada de percentual.
 * Formato: 12,50 %
 *
 * Compatível com react-hook-form via <Controller>:
 *   render={({ field }) => (
 *     <PercentageInput
 *       value={field.value}
 *       onValueChange={(values) => field.onChange(values.floatValue ?? '')}
 *       onBlur={field.onBlur}
 *       ...
 *     />
 *   )}
 */
const PercentageInput = forwardRef(({
  label,
  error,
  hint,
  onValueChange,
  value,
  fullWidth = true,
  containerClassName,
  className,
  required,
  max = 9999,
  decimalScale = 2,
  ...props
}, ref) => {
  const inputClass = clsx(
    'block w-full rounded-lg border transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500',
    'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
    'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100',
    'pl-4 pr-4 py-2',
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
      : 'border-gray-300 dark:border-gray-600',
    className
  );

  return (
    <div className={clsx(fullWidth && 'w-full', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <NumericFormat
        getInputRef={ref}
        value={value ?? ''}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={decimalScale}
        suffix=" %"
        allowNegative={false}
        isAllowed={({ floatValue }) => floatValue == null || floatValue <= max}
        onValueChange={onValueChange}
        className={inputClass}
        placeholder="0,00 %"
        {...props}
      />

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}

      {hint && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
      )}
    </div>
  );
});

PercentageInput.displayName = 'PercentageInput';

export default PercentageInput;
